import { Chip } from "@mui/material";
import { DoneIcon } from "~/icons";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CircularSectorProgress from "~/pages/integrations/security-pipeline/security-pipeline-github/SecurityPipelineGithubSpacePage/components/SecurityPipelineGithubPageTabs/SecurityPipelineGithubPageTabOverview/CircularSectorProgress";
import { InitiativeStatus } from "~/operations";
import { useColorMode } from "~/providers/color-mode";
import { useMemo, type ReactElement } from "react";

interface InitiativeStatusChipProps {
  status: InitiativeStatus;
}

export const InitiativeStatusChip = ({ status }: InitiativeStatusChipProps) => {
  const { mode } = useColorMode();
  const isDarkMode = mode === "dark";

  const statusConfig = useMemo(() => {
    const configs: Record<
      | InitiativeStatus.InProgress
      | InitiativeStatus.Fixed
      | InitiativeStatus.Canceled,
      {
        label: string;
        icon: ReactElement;
        backgroundColor: string;
        color?: string;
        iconColor?: string;
      }
    > = {
      [InitiativeStatus.InProgress]: {
        label: "In progress",
        icon: (
          <CircularSectorProgress
            size={9}
            progress={50}
            color="white"
            background="transparent"
            strokeWidth={0.5}
          />
        ),
        backgroundColor: isDarkMode ? "background.lighter" : "#292928",
      },
      [InitiativeStatus.Fixed]: {
        label: "Fixed",
        icon: <DoneIcon sx={{ fontSize: 9 }} />,
        backgroundColor: isDarkMode ? "excellent.light" : "excellent.main",
        color: isDarkMode ? "excellent.contrastText" : "primary.contrastText",
        iconColor: isDarkMode
          ? "excellent.contrastText"
          : "primary.contrastText",
      },
      [InitiativeStatus.Canceled]: {
        label: "Canceled",
        icon: <RemoveCircleOutlineIcon sx={{ fontSize: 9 }} />,
        backgroundColor: isDarkMode ? "background.lighter" : "#292928",
      },
    };

    return configs[status as keyof typeof configs];
  }, [status, isDarkMode]);

  if (!statusConfig) {
    return null;
  }

  return (
    <Chip
      icon={statusConfig.icon}
      label={statusConfig.label}
      size="small"
      sx={{
        borderRadius: "8px",
        fontSize: 12,
        fontWeight: 700,
        padding: "6px 8px",
        height: "auto",
        gap: 1,
        backgroundColor: statusConfig.backgroundColor,
        color: statusConfig.color || "white",
        "& .MuiChip-icon": {
          width: "16px",
          height: "16px",
          m: 0,
          color: statusConfig.iconColor || "white",
        },
        "& .MuiChip-label": {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    />
  );
};
